import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import searchSignalTool from '../conditions/searchSignalTool';
import deleteOutputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {CompoundConditionCardScratchpadState} from '../../scratchpad/compoundConditionCardScratchpad.types';
import getOrCreateCompoundConditionCard from '../../utils/compoundConditions/getOrCreateCompoundConditionCard';
import addCompoundConditionOutputTool from './addCompoundConditionOutputTool';
import editCompoundConditionOutputTool from './editCompoundConditionOutputTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `### What is a Compound Condition card
A Compound Condition card routes traffic using complex multi-signal rules. Each output contains sections (AND logic) with rules (OR logic). Each rule evaluates a signal with an operator and value. The fallback output is selected when no regular output's conditions are met.

### Compound Condition card rules
${FallbackOutputGuidelines}.
- DO NOT add fallback outputs of any kind, only set the outputs requested by the user.

### Property Dependencies
- Signals must be discovered (searchSignal) before adding or editing outputs, because each rule within an output references a signal from the search results and uses that signal's available operators and values.
- Deleting outputs has no dependencies on signal search.`;

export default function addOrUpdateCompoundConditionCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<
      typeof addOrUpdateCardInputSchema,
      CompoundConditionCardScratchpadState
    >,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateCompoundConditionCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateCompoundConditionCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.COMPOUND_CONDITION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.COMPOUND_CONDITION,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        deleteOutputsTool(),
        getFilesTool(),
        getCardDetailsTool(),
        readValuesFromExcelTool(),

        // Compound condition tools
        searchSignalTool(store, NodeType.COMPOUND_CONDITION),
        addCompoundConditionOutputTool(),
        editCompoundConditionOutputTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateCompoundConditionCard,
    description:
      'Add or update a Compound Condition card. Supports multiple outputs, each with sections (AND logic) containing rules (OR logic). Each rule has a signal, operator, and value. This tool should be used for complex (NOT SINGULAR) conditions.',
    displayName: 'Working on compound condition card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
